import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "../../../shared/hooks/useApi";
import { useCallbackRef } from "../../../shared/hooks/useCallbackRef";
import {
  components,
  PatientId,
  PatientMedicalDiagnosisCodeId,
} from "../../../shared/schema/schema";
import EntityModalButton from "../../entityProfileModal/components/EntityModalButton";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../entityProfileModal/components/EntityProfileDataTable";
import {
  EntityProfileEmptyState,
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../entityProfileModal/components/EntityProfileFeedback";
import AddEditMedicalDiagnosisCodeModal from "./AddEditMedicalDiagnosisCodeModal";

type MedicalDiagnosisCode = components["schemas"]["PatientMedicalDiagnosisCode"];
type MedicalDiagnosisCodeSortKey = "designation" | "dxCode" | "shortDescription" | "source";

interface Props {
  /** Shows the codes without add / edit / delete. */
  isReadOnly?: boolean;
  patientId: PatientId;
  showTitle?: boolean;
  /** After each successful add, edit or delete. */
  onChanged?: () => void;
  /** Whether an add / edit / delete dialog is open (closing the page would lose it). */
  onFormOpenChange?: (isOpen: boolean) => void;
}

function formatDesignation(designation: MedicalDiagnosisCode["designation"]) {
  switch (designation) {
    case "PRIMARY":
      return "Primary";
    case "OTHER":
      return "Other";
  }
}

export default function MedicalDiagnosisCodeTable({
  isReadOnly = false,
  patientId,
  showTitle = true,
  onChanged,
  onFormOpenChange,
}: Props) {
  const queryClient = useQueryClient();
  const { queries, mutations } = useApi();
  const [editingCode, setEditingCode] = useState<MedicalDiagnosisCode | null>(null);
  const [deletingCode, setDeletingCode] = useState<MedicalDiagnosisCode | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const diagnosisCodes = useQuery(queries.patient.medicalDiagnosisCodes(patientId));

  const deleteMutation = useMutation({
    ...mutations.patient.deleteMedicalDiagnosisCode(
      deletingCode?.id ?? (0 as PatientMedicalDiagnosisCodeId)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.patient.medicalDiagnosisCodes(patientId).queryKey,
      });
      handleDeleteModalClose();
      onChanged?.();
    },
  });

  const reportFormOpen = useCallbackRef(onFormOpenChange);
  const isFormOpen = isAddModalOpen || editingCode !== null || isDeleteModalOpen;
  useEffect(() => {
    reportFormOpen(isFormOpen);
  }, [isFormOpen, reportFormOpen]);

  const handleEdit = useCallback((code: MedicalDiagnosisCode) => {
    setEditingCode(code);
  }, []);

  const handleDelete = useCallback(
    (code: MedicalDiagnosisCode) => {
      setDeletingCode(code);
      onDeleteModalOpen();
    },
    [onDeleteModalOpen]
  );

  const handleEditModalClose = () => {
    setEditingCode(null);
  };

  const handleDeleteModalClose = () => {
    setDeletingCode(null);
    onDeleteModalClose();
  };

  const confirmDelete = () => {
    if (deletingCode) {
      deleteMutation.mutate();
    }
  };

  const columns = useMemo<
    Array<EntityProfileDataTableColumn<MedicalDiagnosisCode, MedicalDiagnosisCodeSortKey>>
  >(
    () => [
      {
        header: "DX Code",
        key: "dxCode",
        render: (code) => <Text fontWeight={700}>{code.formattedDxCode}</Text>,
        sortKey: "dxCode",
        sortValue: (code) => code.formattedDxCode,
      },
      {
        header: "Description",
        key: "description",
        render: (code) => code.shortDesc,
        sortKey: "shortDescription",
        sortValue: (code) => code.shortDesc,
      },
      {
        header: "Designation",
        key: "designation",
        render: (code) => formatDesignation(code.designation),
        sortKey: "designation",
        sortValue: (code) => formatDesignation(code.designation),
      },
      {
        header: "Source",
        key: "source",
        render: (code) => code.source ?? "",
        sortKey: "source",
        sortValue: (code) => code.source ?? "",
      },
      {
        cellProps: { width: "120px" },
        header: "Actions",
        key: "actions",
        render: (code) => (
          <HStack spacing={2}>
            <EntityModalButton
              isIconOnly
              aria-label="Edit diagnosis code"
              iconClassName="fa fa-pencil-square-o"
              kind="white"
              title="Edit"
              onClick={() => handleEdit(code)}
            />
            <EntityModalButton
              isIconOnly
              aria-label="Delete diagnosis code"
              iconClassName="fa fa-trash"
              kind="red"
              title="Delete"
              onClick={() => handleDelete(code)}
            />
          </HStack>
        ),
      },
    ],
    [handleDelete, handleEdit]
  );
  const shownColumns = isReadOnly ? columns.filter((column) => column.key !== "actions") : columns;

  if (diagnosisCodes.status === "error") {
    return (
      <EntityProfileErrorState
        label="Could not load medical diagnosis codes."
        onRetry={() => void diagnosisCodes.refetch()}
      />
    );
  }

  if (diagnosisCodes.status === "pending") {
    return <EntityProfileLoadingState label="Loading medical diagnosis codes..." />;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify={showTitle ? "space-between" : "flex-end"}>
        {showTitle ? (
          <Text fontSize="lg" fontWeight={600}>
            Medical Diagnosis Codes
          </Text>
        ) : null}
        {!isReadOnly && (
          <EntityModalButton
            iconClassName="fa fa-plus"
            kind="blue"
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Medical Diagnosis
          </EntityModalButton>
        )}
      </HStack>

      {diagnosisCodes.data.length === 0 ? (
        <Box py={8}>
          <EntityProfileEmptyState>
            No medical diagnosis codes found.
            {!isReadOnly && " Click “Add Medical Diagnosis” to get started."}
          </EntityProfileEmptyState>
        </Box>
      ) : (
        <EntityProfileDataTable
          columns={shownColumns}
          defaultSort={{ key: "dxCode", direction: "asc" }}
          getRowKey={(code) => code.id}
          rows={diagnosisCodes.data}
          showEmptyState={false}
        />
      )}

      <AddEditMedicalDiagnosisCodeModal
        isOpen={isAddModalOpen}
        patientId={patientId}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={onChanged}
      />

      <AddEditMedicalDiagnosisCodeModal
        key={editingCode?.id ?? "edit"}
        diagnosisCode={editingCode}
        isOpen={editingCode !== null}
        patientId={patientId}
        onClose={handleEditModalClose}
        onSuccess={onChanged}
      />

      <Modal isOpen={isDeleteModalOpen} onClose={handleDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Medical Diagnosis Code</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete{" "}
              <Text as="span" fontWeight={700}>
                {deletingCode?.formattedDxCode} - {deletingCode?.shortDesc}
              </Text>
              ? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={handleDeleteModalClose}>
              Cancel
            </Button>
            <EntityModalButton
              disabled={deleteMutation.isPending}
              kind="red"
              onClick={confirmDelete}
            >
              Delete
            </EntityModalButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
